import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { lazy, Suspense } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { HelmetProvider } from "react-helmet-async";
import ScrollToTop from "./components/ScrollToTop.tsx";
import CookieConsent from "./components/CookieConsent.tsx";

import Index from "./pages/Index.tsx";

const Services = lazy(() => import("./pages/Services.tsx"));
const About = lazy(() => import("./pages/About.tsx"));
const CaseStudies = lazy(() => import("./pages/CaseStudies.tsx"));
const Blog = lazy(() => import("./pages/Blog.tsx"));
const BlogPost = lazy(() => import("./pages/BlogPost.tsx"));
const Contact = lazy(() => import("./pages/Contact.tsx"));
const OfstedReports = lazy(() => import("./pages/OfstedReports.tsx"));
const DiagnosticForm = lazy(() => import("./pages/DiagnosticForm.tsx"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe.tsx"));
const InclusionCPD = lazy(() => import("./pages/InclusionCPD.tsx"));
const AIForBusiness = lazy(() => import("./pages/AIForBusiness.tsx"));
const AIReadinessDinner = lazy(() => import("./pages/AIReadinessDinner.tsx"));
const AILeadershipAccelerator = lazy(() => import("./pages/AILeadershipAccelerator.tsx"));
const AwardingOrganisations = lazy(() => import("./pages/AwardingOrganisations.tsx"));
const TrainingProviders = lazy(() => import("./pages/TrainingProviders.tsx"));
const Privacy = lazy(() => import("./pages/Privacy.tsx"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy.tsx"));
const AITransparency = lazy(() => import("./pages/AITransparency.tsx"));
const Terms = lazy(() => import("./pages/Terms.tsx"));
const Accessibility = lazy(() => import("./pages/Accessibility.tsx"));
const NotFound = lazy(() => import("./pages/NotFound.tsx"));


const queryClient = new QueryClient();

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ScrollToTop />
          <CookieConsent />

          <Suspense fallback={<div className="min-h-screen" />}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/index" element={<Navigate to="/" replace />} />
            <Route path="/index.html" element={<Navigate to="/" replace />} />
            <Route path="/services" element={<Services />} />
            <Route path="/about" element={<About />} />
            <Route path="/case-studies" element={<CaseStudies />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/ofsted-reports" element={<OfstedReports />} />
            <Route path="/diagnostic" element={<DiagnosticForm />} />
            <Route path="/unsubscribe" element={<Unsubscribe />} />
            <Route path="/inclusion-cpd" element={<InclusionCPD />} />
            <Route path="/ai-for-business" element={<AIForBusiness />} />
            <Route path="/ai-readiness-dinner" element={<AIReadinessDinner />} />
            <Route path="/ai-leadership-accelerator" element={<AILeadershipAccelerator />} />
            <Route path="/awarding-organisations" element={<AwardingOrganisations />} />
            <Route path="/training-providers" element={<TrainingProviders />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/cookies" element={<CookiePolicy />} />
            <Route path="/ai-transparency" element={<AITransparency />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/accessibility" element={<Accessibility />} />
            <Route path="*" element={<NotFound />} />

          </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
